import { useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { FirebaseError } from 'firebase/app';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';

import { axiosInstance } from '../../axios';
import { db } from '../../config';

/** Adresowanie wysyłki. Jedna osoba to ten sam tryb co wiele, tylko z krótszą listą. */
export type CampaignTarget =
  | { mode: 'users'; userIds: string[] }
  | { mode: 'organizations'; organizationIds: string[] }
  /** Adresy wpisane ręcznie, także takie, które nie mają konta w panelu. */
  | { mode: 'emails'; emails: string[] }
  | { mode: 'all' };

export interface CampaignChannels {
  /** Wiadomość w skrzynce panelu. */
  inApp: boolean;
  /** Zwykły e-mail. */
  email: boolean;
}

export interface SendCampaignRequest {
  subject: string;
  html: string;
  text: string;
  channels: CampaignChannels;
  target: CampaignTarget;
  templateId?: string | null;
  templateName?: string;
}

export interface SendCampaignResult {
  id: string;
  recipientCount: number;
  sentCount: number;
  failedCount: number;
  inboxCount: number;
}

interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

export const IN_MAIL_CAMPAIGNS_QUERY_KEY = ['in-mail-campaigns'] as const;

export const useSendInMailCampaign = (): UseMutationResult<
  SendCampaignResult,
  AxiosError,
  SendCampaignRequest
> => {
  const queryClient = useQueryClient();

  return useMutation<SendCampaignResult, AxiosError, SendCampaignRequest>({
    mutationFn: async (request) => {
      const res = await axiosInstance.post<ApiResponse<SendCampaignResult>>(
        '/v3-sendInMailCampaign',
        request,
        // Wysyłka „do wszystkich" idzie partiami przez SMTP i potrafi trwać minuty,
        // więc domyślny limit axiosa zerwałby połączenie w trakcie.
        { timeout: 540_000 },
      );
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: IN_MAIL_CAMPAIGNS_QUERY_KEY });
    },
  });
};

/** Pojedynczy odbiorca wysyłki razem ze śladem otwarcia. */
export interface CampaignRecipientRecord {
  id: string;
  email: string;
  status: 'sent' | 'failed' | 'queued';
  error?: string;
  openedAt: number | null;
  openCount: number;
}

export interface CampaignRecord {
  id: string;
  subject: string;
  channels: CampaignChannels;
  recipientCount: number;
  sentCount: number;
  failedCount: number;
  openedCount: number;
  inboxCount: number;
  createdAt: number | null;
}

const toMillis = (value: unknown): number | null => {
  const stamp = value as { toMillis?: () => number } | undefined;
  return typeof stamp?.toMillis === 'function' ? stamp.toMillis() : null;
};

/** Historia wysyłek razem z licznikiem otwarć. Zapisuje ją backend, panel tylko czyta. */
export const useGetInMailCampaigns = (): UseQueryResult<CampaignRecord[], FirebaseError> => useQuery<
  CampaignRecord[],
  FirebaseError
>({
  queryKey: IN_MAIL_CAMPAIGNS_QUERY_KEY,
  queryFn: async () => {
    const snapshot = await getDocs(query(collection(db, 'inMailCampaigns'), orderBy('createdAt', 'desc')));
    return snapshot.docs.map((entry) => {
      const data = entry.data();
      return {
        id: entry.id,
        subject: typeof data.subject === 'string' ? data.subject : '',
        channels: (data.channels as CampaignChannels) ?? { inApp: false, email: false },
        recipientCount: Number(data.recipientCount) || 0,
        sentCount: Number(data.sentCount) || 0,
        failedCount: Number(data.failedCount) || 0,
        openedCount: Number(data.openedCount) || 0,
        inboxCount: Number(data.inboxCount) || 0,
        createdAt: toMillis(data.createdAt),
      };
    });
  },
  refetchOnWindowFocus: false,
});

/**
 * Odbiorcy jednej wysyłki. Pobieramy dopiero po wejściu w statystyki, bo przy wysyłce
 * „do wszystkich" to tyle dokumentów, ile kont w panelu.
 */
export const useGetCampaignRecipients = (
  campaignId: string | null,
): UseQueryResult<CampaignRecipientRecord[], FirebaseError> => useQuery<
  CampaignRecipientRecord[],
  FirebaseError
>({
  queryKey: [...IN_MAIL_CAMPAIGNS_QUERY_KEY, campaignId, 'recipients'],
  enabled: !!campaignId,
  queryFn: async () => {
    const snapshot = await getDocs(collection(db, 'inMailCampaigns', campaignId as string, 'recipients'));
    return snapshot.docs.map((entry) => {
      const data = entry.data();
      return {
        id: entry.id,
        email: typeof data.email === 'string' ? data.email : '',
        status: (data.status as CampaignRecipientRecord['status']) ?? 'queued',
        error: typeof data.error === 'string' ? data.error : undefined,
        openedAt: toMillis(data.openedAt),
        openCount: Number(data.openCount) || 0,
      };
    });
  },
  refetchOnWindowFocus: false,
});

export const useDeleteInMailCampaigns = (): UseMutationResult<void, AxiosError, { ids: string[] }> => {
  const queryClient = useQueryClient();

  return useMutation<void, AxiosError, { ids: string[] }>({
    mutationFn: async ({ ids }) => {
      await axiosInstance.post('/v3-deleteInMailCampaigns', { ids });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: IN_MAIL_CAMPAIGNS_QUERY_KEY });
    },
  });
};
